import { queryOptions } from '@tanstack/react-query';
import { chunk } from 'lodash';

import { aesmidas, orbisMiddleware } from '../api';
import {
    BondQuantityType,
    BondQuote,
    PriceType,
    Quote,
    QuoteHistory,
    TimeRange,
} from '@orbis-systems/orbis-api-middleware';
import { SuccessClientResponse } from '@orbis-systems/orbis-api-clients';
import { MidasFxRateQuote } from './types';

export type QuoteEquityQueryParams = {
    symbols: string;
};

export type QuoteIntradayQueryParams = {
    symbol: string;
};

export type QuoteHistoricalQueryParams = {
    symbol: string;
    range?: TimeRange;
};

export type QuoteSearchQueryParams = {
    criteria: string;
    limit?: number;
};

export type QuoteBondsSearchQueryParams = {
    criteria: string;
    limit?: number;
};

export type QuoteGetBondsQueryParams = {
    identifiers: string;
};

export type QuoteGetOptionQueryParams = {
    symbols: string;
    greeks?: boolean;
};

export type QuoteGetOptionHistoricalQueryParams = {
    symbol: string;
    range: TimeRange;
};

export type QuoteFxRateQueryParams = {
    symbols: string[];
};

export type QuoteBondsChartQueryParams = {
    isin: string;
    frequency: '1day' | '15min' | '1min';
    start: string; // YYYY-MM-DD
    end: string; // YYYY-MM-DD
};

type BondsChartData = Array<{
    timestamp: string;
    price: number;
    yield_to_worst: number;
}>;
export type QuoteBondsChartQueryResponse = SuccessClientResponse<BondsChartData>;

export type QuoteEquityQueryResponse = Quote[];
export type QuoteIntradayQueryResponse = QuoteHistory[];
export type QuoteHistoricalQueryResponse = QuoteHistory[];
export type QuoteSearchQueryResponse = Quote[];
export type QuoteBondsSearchQueryResponse = Quote[];
export type QuoteGetBondsQueryResponse = BondQuote | null;
export type QuoteGetOptionQueryResponse = Quote[];
export type QuoteGetOptionHistoricalQueryResponse = QuoteHistory[];
export type QuoteFxRateQueryResponse = Quote[];

export const quoteQueries = {
    all: () => queryOptions({ queryKey: ['quote'] }),
    equity: (params: QuoteEquityQueryParams) =>
        queryOptions<QuoteEquityQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'equity', params],
            queryFn: () => orbisMiddleware.quote.getQuotes(params.symbols.split(',')),
        }),
    intradayChartData: (params: QuoteIntradayQueryParams) =>
        queryOptions<QuoteIntradayQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'intradayChartData', params],
            queryFn: () => orbisMiddleware.stat.getQuoteIntraday(params.symbol),
        }),
    historical: (params: QuoteHistoricalQueryParams) =>
        queryOptions<QuoteHistoricalQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'historical', params],
            queryFn: () => orbisMiddleware.stat.getQuoteHistorical(params.symbol, params.range),
        }),
    search: (params: QuoteSearchQueryParams) =>
        queryOptions<QuoteSearchQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'search', params],
            queryFn: () => orbisMiddleware.quote.searchQuotes(params.criteria, params.limit),
        }),
    bondsSearch: (params: QuoteBondsSearchQueryParams) =>
        queryOptions<QuoteBondsSearchQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'bondsSearch', params],
            queryFn: () => orbisMiddleware.quote.searchBondQuotes(params.criteria, params.limit),
        }),
    getBonds: (params: QuoteGetBondsQueryParams) =>
        queryOptions<QuoteGetBondsQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'getBonds', params],
            queryFn: () => orbisMiddleware.quote.getBondQuote(params.identifiers),
        }),
    getOption: (params: QuoteGetOptionQueryParams) =>
        queryOptions<QuoteGetOptionQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'getOption', params],
            queryFn: () => orbisMiddleware.quote.getOptionQuotes(params.symbols.split(',')),
        }),
    getOptionHistorical: (params: QuoteGetOptionHistoricalQueryParams) =>
        queryOptions<QuoteGetOptionHistoricalQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'getOptionHistorical', params],
            queryFn: () => orbisMiddleware.stat.getOptionHistorical(params.symbol, params.range),
        }),
    fxRate: (params: QuoteFxRateQueryParams) =>
        queryOptions<QuoteFxRateQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'fxRate', params],
            queryFn: () => orbisMiddleware.quote.getQuotesFxRate(params.symbols),
        }),
    bondsChart: (params: QuoteBondsChartQueryParams) =>
        queryOptions<QuoteBondsChartQueryResponse>({
            queryKey: [...quoteQueries.all().queryKey, 'bondsChart', params],
            queryFn: () => aesmidas.getApiClient().get<BondsChartData>('quote/bonds-chart', { params }),
        }),
    getMidasEquityQuotes: (symbols: string[]) => {
        const normalizedSymbols = [...new Set(symbols)].sort();
        return queryOptions<Quote[]>({
            queryKey: [...quoteQueries.all().queryKey, 'getMidasEquityQuotes', normalizedSymbols],
            queryFn: async () => {
                const responses = await Promise.all(
                    chunk(normalizedSymbols, 50).map(async (symbolList) => {
                        return aesmidas.quotesReformat.getEquity({ symbols: symbolList.join(',') });
                    }),
                );
                return responses.flatMap((response) => response.data);
            },
        });
    },
    getMidasBondQuotes: (symbols: string[]) => {
        const normalizedSymbols = [...new Set(symbols)].sort();
        return queryOptions<BondQuote[]>({
            queryKey: [...quoteQueries.all().queryKey, 'getMidasBondQuotes', normalizedSymbols],
            queryFn: async () => {
                const responses = await Promise.all(
                    chunk(normalizedSymbols, 20).map(async (symbolList) => {
                        return aesmidas.quotesReformat.getBonds({
                            identifiers: symbolList.join(','),
                            bondQuantityType: BondQuantityType.Par,
                            bondPriceType: PriceType.Decimal,
                        });
                    }),
                );
                return responses.flatMap((response) => response.data);
            },
        });
    },
    getMidasOptionQuotes: (symbols: string[]) => {
        const normalizedSymbols = [...new Set(symbols)].sort();
        return queryOptions<Quote[]>({
            queryKey: [...quoteQueries.all().queryKey, 'getMidasOptionQuotes', normalizedSymbols],
            queryFn: async () => {
                const responses = await Promise.all(
                    chunk(normalizedSymbols, 50).map(async (symbolList) => {
                        return aesmidas.quotesReformat.getOption({ symbols: symbolList.join(','), greeks: false });
                    }),
                );
                return responses.flatMap((response) => response.data);
            },
        });
    },
    getMidasFxRates: (symbols: string[]) => {
        const normalizedSymbols = [...new Set(symbols)].sort();
        return queryOptions<MidasFxRateQuote[]>({
            queryKey: [...quoteQueries.all().queryKey, 'getMidasFxRates', normalizedSymbols],
            queryFn: async () => {
                const response = await aesmidas.quotesReformat.getQuotesReformat({
                    symbols: normalizedSymbols.join(','),
                });
                return response.data;
            },
        });
    },
};
